import MenuItem from '@mui/material/MenuItem'
import {SelectChangeEvent} from '@mui/material/Select'
import {useEffect, useMemo, useState} from 'react'
import {useDispatch} from 'react-redux'
import {
  DisableAutoCharge,
  GetAutoCharge,
  GetPackages,
  GetTokens,
  PackageType,
  SetAutoCharge,
  setPackageModalOpen,
  setPackages,
  TokenType,
  usePaymentPackages
} from 'entities/payment'
import {CardItem} from 'features/cardItem'
import {NoResults} from 'features/noResults'
import {SelectWithLabel} from 'features/selectWithLabel'
import {Button} from 'shared/ui/buttons'
import {Toggle} from 'shared/ui/toggle'
import styles from './styles.module.scss'

export const Billing = () => {
  const dispatch = useDispatch()
  const [autoChargeState, setAutoChargeState] = useState({isEnabled: false, idToken: '', idPackage: 0})
  const [isFetched, setIsFetched] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [tokens, setTokens] = useState<TokenType[]>([])
  const isEmptyState = useMemo(() => tokens.length === 0, [tokens.length])
  const packages = usePaymentPackages()

  const updateAutoCharge = ({idPackage = autoChargeState.idPackage, idToken = autoChargeState.idToken}: {
    idPackage?: number,
    idToken?: string
  }) => {
    if (!autoChargeState.isEnabled) return
    setIsLoading(true)

    SetAutoCharge({idPackage, idToken})
      .catch(console.error)
      .finally(() => setIsLoading(false))
  }

  const onClickHandler = () => {
    if (isLoading) return
    setIsLoading(true)

    setAutoChargeState((prevState) => {
      if (prevState.isEnabled) {
        DisableAutoCharge()
          .catch(console.error)
          .finally(() => setIsLoading(false))
      }

      if (!prevState.isEnabled) SetAutoCharge({idPackage: autoChargeState.idPackage, idToken: autoChargeState.idToken})
        .catch(console.error)
        .finally(() => setIsLoading(false))

      return ({...prevState, isEnabled: !prevState.isEnabled})
    })
  }

  const onChangePackageHandler = (e: SelectChangeEvent<unknown>) => {
    if (isLoading) return

    const idPackage = Number(e.target.value)

    setAutoChargeState((prevState) => ({...prevState, idPackage}))

    updateAutoCharge({idPackage})
  }

  const onChangeToken = (idToken: string) => {
    if (idToken === autoChargeState.idToken || isLoading) return

    setAutoChargeState(prevState => ({...prevState, idToken}))

    updateAutoCharge({idToken})
  }

  const selectPackageHandler = () => dispatch(setPackageModalOpen(true))

  const onSuccessDelete = (cardId: string) => {
    const newTokens = tokens.filter((token) => token.id !== cardId)
    setTokens(newTokens)
    //change new active token after delete
    if (cardId === autoChargeState.idToken && newTokens.length)
      onChangeToken(newTokens[0].id)
  }

  useEffect(() => {
    const prepareState: {packages: PackageType[], tokens: TokenType[]} = {packages: [], tokens: []}

    GetPackages()
      .then(({data}) => {
        dispatch(setPackages(data.data))
        prepareState.packages = data.data
        return GetTokens()
      })
      .then(({data}) => {
        setTokens(data.data)
        prepareState.tokens = data.data
        return GetAutoCharge()
      })
      .then(({data}) => {
        if (!data) {
          return setAutoChargeState({
            isEnabled: false,
            idToken: prepareState.tokens[0]?.id || '',
            idPackage: prepareState.packages[prepareState.packages.length - 1]?.id || 0
          })
        }

        setAutoChargeState({
          idToken: data.token.id,
          idPackage: data.package.id,
          isEnabled: true
        })
      })
      .finally(() => setIsFetched(true))
      .catch(console.error)
  }, [])

  return (
    <div className={styles.billing}>
      {isEmptyState && isFetched ? <div className={styles.emptyState}>
        <div className={styles.emptyState__container}>
          <NoResults
            header='No saved payment methods'
            text='The interface will become active after purchasing one of the packages via the "Add new method" button.'
            className={styles.noResults}
          />
          <Button className={styles.selectPackageBtn} onClick={selectPackageHandler} styleType='primary'>
            Select package
          </Button>
        </div>
      </div> : null}
      <div className={`${styles.wrapper} ${isEmptyState ? styles.wrapper_empty : null}`}>
        <div className={styles.marketing}>
          <div className={styles.textWrapper}>
            <h5 className={styles.title}>
              Automatic Top-Up
            </h5>
            <p className={styles.description}>
              You've enabled automatic deductions from your payment method every time (based on the plan you choose)
              when
              your balance drops below 20 GoCoins until you turn it off here.
            </p>
          </div>
          <Toggle value={autoChargeState.isEnabled} onClick={onClickHandler} />
        </div>
        <div className={styles.textWrapper}>
          <SelectWithLabel
            onChange={onChangePackageHandler}
            value={autoChargeState.idPackage}
            name='PackageType'
            label='Package type'
          >
            {packages.map(item =>
              <MenuItem key={item.id} value={item.id}>{item.credits} GoCoins (${item.price})</MenuItem>)}
          </SelectWithLabel>
          <p className={styles.description}>
            To ensure continued service, we’ll store your payment method. By placing this order you agree to our Terms
            of
            Use.
          </p>
        </div>
        {tokens.length > 0 &&
          <div className={styles.textWrapper}>
            <h5 className={styles.title}>Payment method</h5>
            <div className={styles.cardWrapper}>
              {tokens.map((item) => (
                <CardItem
                  onSuccessDelete={() => onSuccessDelete(item.id)}
                  key={item.id}
                  withDelete
                  value={autoChargeState.idToken === item.id}
                  onClick={() => onChangeToken(item.id)}
                  {...item}
                />
              ))}
            </div>
          </div>
        }
      </div>
    </div>
  )
}